/** @module csi/widget/Dialog */
define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"dojo/dom-style",
	"dojo/when",
	"dojo/window",
	"dijit/focus",
	"dijit/Dialog",
	"dijit/DialogUnderlay",
	"csi/html",
	"csi/dndHTML5",
	"csi/utility/htmlUtility",
	"./_DialogMixin"
], function(
	declare,
	domClass,
	domStyle,
	when,
	winUtils,
	DijitFocus,
	DijitDialog,
	DialogUnderlay,
	html,
	dndHTML5,
	{
		getAllDialogs,
		getReactDialogs,
		isReactDialogWithHighestZIndex,
	},
	_DialogMixin
) {

	/** @property {boolean} _disableFocus - force disable focus for dialog, such as when a React dialog is open above a Dojo one */
	let _disableFocus = false;

	const Dialog = declare([DijitDialog, _DialogMixin], {
		_setTitleAttr: {node: "titleNode", type: "innerText"},

		/**
		 * Only focus dialog if it is on top of any react dialogs and inline editing is not active
		 * Reference dijit/Dialog:
		 * 		focus.watch("curNode", function(attr, oldNode, node){
		 */
		focus() {
			if (!isReactDialogWithHighestZIndex() && !_disableFocus) {
				this.inherited(arguments);
			}
		},

		postCreate: function() {
			this.inherited(arguments);
			this.closeText.className = html.CSS_MATERIAL_ICON;
			this.closeText.innerHTML = "close";
			html.setVisible(this.closeText, true);
		},

		/**
		 * Update maxRatio based on current conditions
		 * @param {Object} dim - dimension box, not used here but in dijit library sometimes
		 */
		resize: function(dim) {
			this.maxRatio = this._getViewportRatio();
			this.inherited(arguments);
		},

		/**
		 * Don't snap to viewport bounds when moving dialog
		 */
		_endDrag: function() { },

		/**
		 * Return ratio to use to calculate available dialog space in viewport
		 * We have couple of dialogs which require a lot of vertical space: dual tables is a case.
		 * For those dialogs we need to consider a situation, when app is running on the device with small screen resolution.
		 * Larger dialog requires minimum 681px height. Standard maxRation (defined in DOJO) is 0.9. 681/0.9=757px (rounding up to 760)
		 * If our view port is less then 760, then we set maxRation to 1, which will allow the dialog to use all avaialble height.
		 * It is also useful for other dialogs, allow to use more space for its needs.
		 * @return {Number}
		 */
		_getViewportRatio: function() {
			var viewport = winUtils.getBox(this.ownerDocument);
			return (viewport.h <= 760) ? 1 : 0.9;
		},

		/**
		 * Set CSS class on document body that indicates opened dialog
		 */
		show: function() {
			when(this.inherited(arguments), () => {
				this._setDialogActiveCSS();
			});
		},

		/**
		 * Remove CSS class on document body that indicates opened dialog
		 */
		hide: function() {
			return when(this.inherited(arguments), () => {
				this._setDialogActiveCSS();
			});
		},

		/**
		 * Toggle "csi-dialog" class on document body; depends on dialog count
		 */
		_setDialogActiveCSS: function() {
			const dialogsCount = getAllDialogs().length;
			// We have to consider dialogs count because error messages are also dialogs and can be opened from within of real dialog
			dndHTML5.setDialogLevel(dialogsCount);
			domClass.toggle(document.body, "csi-dialog", dialogsCount > 0);
		}
	});

	// change the starting zIndex for internal dijit dialogs from 950 to 800, which is the same as our React dialogs
	DijitDialog._DialogLevelManager._beginZIndex = 800

	// The following code is mostly the same as what's in dijit/Dialog.
	// Some changes were made to access internal objects. For example, ds was set to DijitDialog._dialogStack.
	// This is a way to tap into the underlying Dojo "show dialog" z-index logic present within DialogLevelManager.
	// If there is ever a case that a Dojo view is launched from a React Dialog, we will need to take those into account.
	DijitDialog._DialogLevelManager.show = function(/*dijit/_WidgetBase*/ dialog, /*Object*/ underlayAttrs){
		// summary:
		//		Call right before fade-in animation for new dialog.
		//		Saves current focus, displays/adjusts underlay for new dialog,
		//		and sets the z-index of the dialog itself.
		//
		//		New dialog will be displayed on top of all currently displayed dialogs.
		//
		//		Caller is responsible for setting focus in new dialog after the fade-in
		//		animation completes.

		const ds = DijitDialog._dialogStack;

		// Save current focus
		ds[ds.length - 1].focus = DijitFocus.curNode;

		// Set z-index a bit above previous dialog
		let zIndex = ds[ds.length - 1].dialog ? ds[ds.length - 1].zIndex + 2 : DijitDialog._DialogLevelManager._beginZIndex;

		// take react dialogs into account
		const reactDialogs = getReactDialogs();
		const { size } = reactDialogs;
		if (size) {
			zIndex = Math.max(..._.compact(reactDialogs).map((x) => parseInt(getComputedStyle(x).zIndex)), zIndex + 2);
		}

		domStyle.set(dialog.domNode, 'zIndex', zIndex);

		// Display the underlay, or if already displayed then adjust for this new dialog
		const underlayZ = zIndex - 1;
		if (DialogUnderlay.getDisabled()) {
			// if disabled, just adjust zIndex
			domStyle.set(DialogUnderlay._singleton.domNode, 'zIndex', underlayZ);
		} else {
			DialogUnderlay.show(underlayAttrs, underlayZ);
		}

		ds.push({dialog: dialog, underlayAttrs: underlayAttrs, zIndex: zIndex});
	},

	/**
	 * Force disable focus for Dojo dialogs
	 * @param {boolean} disable 
	 */
	Dialog.disableDojoDialogFocus = (disable) => {
		_disableFocus = disable;
	};

	return Dialog;
});